import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, LogOut, HeartPulse, CheckCircle2, Loader2, AlertCircle, LayoutGrid } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { WELLNESS_NAV } from "@/components/nav";
import { useAuth } from "@/context/AuthContext";
import { useWellnessState } from "@/context/WellnessStateContext";

const SaveIndicator = () => {
  const { saveStatus } = useWellnessState();
  const map = {
    saving: { icon: Loader2, text: "Saving…", cls: "text-[var(--text-muted)]", spin: true },
    saved: { icon: CheckCircle2, text: "Saved", cls: "text-[var(--green)]" },
    error: { icon: AlertCircle, text: "Save failed", cls: "text-[var(--danger)]" },
    idle: { icon: CheckCircle2, text: "All changes saved", cls: "text-[var(--text-faint)]" },
  };
  const s = map[saveStatus] || map.idle;
  const Icon = s.icon;
  return (
    <div className={cn("hidden sm:flex items-center gap-1.5 text-xs", s.cls)} data-testid="wellness-save-indicator">
      <Icon className={cn("h-3.5 w-3.5", s.spin && "animate-spin")} />
      <span>{s.text}</span>
    </div>
  );
};

const NavList = ({ onNavigate }) => {
  const { pathname } = useLocation();
  return (
    <nav className="px-3 py-2 space-y-1">
      {WELLNESS_NAV.map((item) => {
        const active = item.to === "/wellness" ? pathname === "/wellness" : pathname.startsWith(item.to);
        const Icon = item.icon;
        return (
          <NavLink key={item.key} to={item.to} end={item.to === "/wellness"} onClick={onNavigate}
            data-testid={`wellness-nav-item-${item.key}`}
            className={cn("group relative flex items-center gap-3 rounded-[12px] px-3 py-2 text-sm transition-colors",
              active ? "bg-[color:rgba(31,115,85,0.12)] text-[var(--text)] font-medium"
                : "text-[var(--text-muted)] hover:bg-[color:rgba(31,115,85,0.07)] hover:text-[var(--text)]")}>
            {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[var(--green)]" />}
            <Icon className={cn("h-[18px] w-[18px]", active ? "text-[var(--green)]" : "text-[color:rgba(23,35,31,0.6)] group-hover:text-[var(--green)]")} />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

const Brand = () => (
  <div className="flex items-center gap-2.5 px-5 py-5">
    <span className="grid place-items-center h-9 w-9 rounded-[10px] bg-[var(--green)] text-[#f7fbf9]"><HeartPulse className="h-5 w-5" /></span>
    <div className="leading-tight">
      <div className="font-serif-display text-[15px] text-[var(--text)]">Wellness Command</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Health &amp; Glow</div>
    </div>
  </div>
);

const SwitchButton = () => {
  const navigate = useNavigate();
  return (
    <Button variant="outline" onClick={() => navigate("/")} data-testid="switch-center-button"
      className="w-full justify-start border-[color:var(--border-subtle)] text-[var(--text)] hover:bg-[color:rgba(198,139,60,0.10)] mb-2">
      <LayoutGrid className="h-4 w-4 mr-2 text-[var(--gold)]" /> Switch command center
    </Button>
  );
};

export default function WellnessLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      <aside data-testid="wellness-sidebar" className="hidden lg:flex flex-col w-72 shrink-0 border-r border-[color:var(--border-subtle)] bg-[var(--surface-1)] sticky top-0 h-screen">
        <Brand />
        <ScrollArea className="flex-1"><NavList /></ScrollArea>
        <div className="border-t border-[color:var(--border-subtle)] p-3">
          <SwitchButton />
          <div className="flex items-center gap-3 px-2 py-1.5">
            <span className="grid place-items-center h-8 w-8 rounded-full bg-[color:rgba(31,115,85,0.14)] text-[var(--green)] text-sm font-medium">{(user?.display_name || "U").charAt(0).toUpperCase()}</span>
            <div className="min-w-0 flex-1"><div className="text-sm text-[var(--text)] truncate">{user?.display_name}</div><div className="text-[11px] text-[var(--text-faint)] truncate">{user?.email}</div></div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="wellness-sign-out-button" aria-label="Sign out" className="h-8 w-8 text-[var(--text-muted)] hover:text-[var(--danger)]"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-[color:var(--border-subtle)] bg-[color:rgba(251,250,246,0.85)] backdrop-blur px-4 sm:px-6 h-14">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden" data-testid="wellness-mobile-nav-open" aria-label="Open navigation"><Menu className="h-5 w-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-[var(--surface-1)]">
                <Brand /><ScrollArea className="h-[calc(100vh-200px)]"><NavList onNavigate={() => setOpen(false)} /></ScrollArea>
                <div className="border-t border-[color:var(--border-subtle)] p-3"><SwitchButton /><Button variant="ghost" onClick={logout} className="w-full justify-start text-[var(--danger)]"><LogOut className="h-4 w-4 mr-2" /> Sign out</Button></div>
              </SheetContent>
            </Sheet>
            <span className="lg:hidden font-serif-display text-[var(--text)]">Wellness Command</span>
          </div>
          <SaveIndicator />
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] w-full"><Outlet /></main>
      </div>
    </div>
  );
}
